import Button from '@orn/shared-web/components/Button/Button';
import * as React from 'react';

export interface ICardEntryBaseProps {
  /**
   * The mounted payment element, or null while the setup is being opened.
   * A slot rather than mounted here so this stays renderable without a live
   * Stripe client — stories and tests pass a stand-in.
   */
  paymentElement: React.ReactNode | null;
  /**
   * Whether a confirm can actually run. The element mounts before Stripe and
   * its hooks are ready, so a rendered slot alone must not enable Continue —
   * a click in that window would silently do nothing.
   */
  canSubmit: boolean;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  errorMessage?: string;
}

/**
 * Card entry for the fallback rail — the screen "pay by card instead" opens.
 *
 * Renders the chrome around the Stripe Payment Element: the element collects
 * the card (wallets first, per its own configuration), and the confirm here
 * only authorizes the setup — nothing is charged until the authorize screen's
 * subscribe, which the flow rejoins once the card is confirmed.
 */
const CardEntryBase = ({
  paymentElement,
  canSubmit,
  onSubmit,
  onBack,
  isSubmitting,
  errorMessage,
}: ICardEntryBaseProps) => (
  <div className="flex w-full flex-col gap-6">
    <div className="flex w-full flex-col gap-2">
      <p className="text-primary text-lg font-medium">Pay by card</p>
      <p className="text-tertiary text-sm">Nothing is charged today. Your card is only saved for after the trial.</p>
    </div>

    {paymentElement ?? (
      <div className="bg-secondary h-24 w-full animate-pulse rounded-lg" aria-label="Loading card form" />
    )}

    {errorMessage !== undefined && <p className="text-error-primary text-sm">{errorMessage}</p>}

    <div className="flex w-full flex-col gap-3">
      <Button
        type="button"
        size="xl"
        color="primary"
        className="w-full"
        onClick={onSubmit}
        isDisabled={isSubmitting || !canSubmit}
      >
        {isSubmitting ? 'Saving card…' : 'Continue'}
      </Button>
      <Button type="button" size="md" color="link-gray" onClick={onBack} isDisabled={isSubmitting}>
        Back
      </Button>
    </div>
  </div>
);

CardEntryBase.displayName = 'CardEntryBase';

export default CardEntryBase;
